import { Link } from "react-router-dom";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { ButtonComponentProps } from "@b2bportal/core-ui";
import clsx from "clsx";
import defaultStyles from "./ActionButton.module.scss";

export interface ActionButtonProps
  extends Omit<ButtonComponentProps, "onClick"> {
  message?: React.ReactNode;
  defaultStyle?: "outlined" | "filled" | "underlined" | "squared-outlined";
  size?: "default" | "large";
  to?: string;
  target?: string;
  rel?: string;
  onClick?: (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => void;
}

export const ActionButton = ({
  message,
  disabled,
  defaultStyle: buttonStyle = "filled",
  onClick,
  className,
  size = "default",
  to,
  target = "_blank",
  rel = "noopener noreferrer",
}: ActionButtonProps) => {
  const [, cn] = useModuleBEM(defaultStyles, "ActionButton");

  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    if (onClick) {
      event.preventDefault();
      event.stopPropagation();
      onClick(event);
    }
  };

  const classNames = clsx(cn(buttonStyle, { disabled }), cn(size), className);

  // If "to" prop is provided, render as Link or anchor
  if (to) {
    // Check if it's an external URL
    const isExternal =
      to.startsWith("http://") ||
      to.startsWith("https://") ||
      to.startsWith("//");

    if (isExternal) {
      return (
        <a
          href={to}
          className={classNames}
          onClick={handleClick}
          target={target}
          rel={rel}
        >
          {message}
        </a>
      );
    }

    // Internal route - use Link component
    return (
      <Link
        to={to}
        className={classNames}
        onClick={handleClick}
        target={target}
        rel={rel}
      >
        {message}
      </Link>
    );
  }

  // Otherwise, render as button
  return (
    <button className={classNames} onClick={handleClick} disabled={disabled}>
      {message}
    </button>
  );
};
